import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Message,
  Modal,
  Radio,
  Segment,
  Step,
  TextArea,
  Dropdown,
} from 'semantic-ui-react';
import type { ZenviaTemplateCategory, ZenviaTemplateCreatePayload, ZenviaTemplatePatchPayload } from '../../types/zenvia.types';
import {
  type TemplateWizardState,
  type HeaderKind,
  type MessageLayout,
  type WizardStep,
  createInitialWizardState,
  validateWizardState,
  buildZenviaWhatsappTemplatePayload,
  buildZenviaWhatsappTemplatePatchPayload,
  syncVariableExamples,
  extractPlaceholders,
} from './zenviaTemplateWizard.utils';
import { ZENVIA_WHATSAPP_SENDER_DROPDOWN_OPTIONS } from './zenviaWhatsappSenders';

const DROPDOWN_MENU_Z = { style: { zIndex: 3100 } as React.CSSProperties };

const CATEGORY_OPTIONS: { key: string; text: string; value: ZenviaTemplateCategory }[] = [
  { key: 'MARKETING', text: 'Marketing', value: 'MARKETING' },
  { key: 'UTILITY', text: 'Utilitário', value: 'UTILITY' },
  { key: 'AUTHENTICATION', text: 'Autenticação', value: 'AUTHENTICATION' },
];

const LOCALE_OPTIONS = [
  { key: 'pt_BR', text: 'Português (Brasil) — pt_BR', value: 'pt_BR' },
  { key: 'en', text: 'English — en', value: 'en' },
  { key: 'en_US', text: 'English (US) — en_US', value: 'en_US' },
  { key: 'es', text: 'Español — es', value: 'es' },
  { key: 'es_AR', text: 'Español (Argentina) — es_AR', value: 'es_AR' },
];

function categoryHelp(category: ZenviaTemplateCategory): { title: string; body: string } {
  switch (category) {
    case 'UTILITY':
      return {
        title: 'Utilitário',
        body:
          'Evite termos de promoção (oferta, desconto, pesquisa etc.) e contextos de boas-vindas ou convites, para não ser classificado como Marketing.',
      };
    case 'MARKETING':
      return {
        title: 'Marketing',
        body:
          'Mensagens promocionais. Considere botão de descadastro (opt-out) para melhorar aprovação e reduzir bloqueios.',
      };
    case 'AUTHENTICATION':
      return {
        title: 'Autenticação',
        body:
          'Fluxos de OTP/códigos. O corpo pode ser omitido conforme regras da Zenvia; use o assistente com cuidado ou o painel Zenvia para fluxos complexos.',
      };
    default:
      return { title: '', body: '' };
  }
}

/** `onSubmit` é tipado conforme `mode` (create = payload completo; edit = PATCH parcial). */
export type WhatsAppTemplateWizardProps =
  | {
      open: boolean;
      mode: 'create';
      loading?: boolean;
      /** Ignorado em create. */
      initialState?: TemplateWizardState | null;
      onClose: () => void;
      onSubmit: (payload: ZenviaTemplateCreatePayload) => Promise<void>;
    }
  | {
      open: boolean;
      mode: 'edit';
      loading?: boolean;
      initialState?: TemplateWizardState | null;
      onClose: () => void;
      onSubmit: (payload: ZenviaTemplatePatchPayload) => Promise<void>;
    };

const WhatsAppTemplateWizard: React.FC<WhatsAppTemplateWizardProps> = (props) => {
  const { open, mode, loading = false, initialState, onClose } = props;
  const [state, setState] = useState<TemplateWizardState>(createInitialWizardState);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const senderDropdownOptions = useMemo(() => {
    const v = state.senderId.trim();
    const base = ZENVIA_WHATSAPP_SENDER_DROPDOWN_OPTIONS;
    if (!v || base.some((o) => o.value === v)) return base;
    return [...base, { key: `sender-${v}`, text: `Remetente atual (API): ${v}`, value: v }];
  }, [state.senderId]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setState(createInitialWizardState());
    } else if (initialState) {
      setState({ ...initialState, step: 1 });
    }
    setSubmitError(null);
  }, [open, mode, initialState]);

  const help = useMemo(() => categoryHelp(state.category), [state.category]);

  const varsInTexts = useMemo(() => {
    const t = [state.bodyText];
    if (state.messageLayout === 'full' && state.headerKind === 'text') t.push(state.headerText);
    return extractPlaceholders(...t);
  }, [state.bodyText, state.headerText, state.messageLayout, state.headerKind]);

  useEffect(() => {
    setState((s) => ({ ...s, variableExamples: syncVariableExamples(s) }));
  }, [varsInTexts]);

  const setStep = (step: WizardStep) => setState((s) => ({ ...s, step }));

  const goNext = () => {
    setSubmitError(null);
    if (state.step === 1) {
      if (!state.name.trim()) {
        setSubmitError('Informe o nome do modelo.');
        return;
      }
      if (!/^[\w]+$/.test(state.name.trim())) {
        setSubmitError('Nome: use apenas letras, números e sublinhado.');
        return;
      }
      setStep(2);
      return;
    }
    if (state.step === 2) {
      setStep(3);
    }
  };

  const goPrev = () => {
    setSubmitError(null);
    if (state.step === 2) setStep(1);
    else if (state.step === 3) setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const err = validateWizardState(state);
    if (err) {
      setSubmitError(err);
      return;
    }
    try {
      if (props.mode === 'create') {
        await props.onSubmit(buildZenviaWhatsappTemplatePayload(state));
      } else {
        await props.onSubmit(buildZenviaWhatsappTemplatePatchPayload(state));
      }
    } catch {
      /* erro tratado pelo pai via toast / estado */
    }
  };

  const bodyRemaining = 1024 - state.bodyText.length;
  const headerRemaining = 60 - state.headerText.length;

  const previewLines = () => {
    const lines: string[] = [];
    if (state.messageLayout === 'full' && state.headerKind === 'text' && state.headerText.trim()) {
      lines.push(`【${state.headerText}】`);
    }
    if (state.messageLayout === 'full' && state.headerKind !== 'none' && state.headerKind !== 'text') {
      lines.push(`[${state.headerKind}: mídia]`);
    }
    lines.push(state.bodyText.trim() || '…');
    if (state.messageLayout === 'full' && state.footerEnabled && state.footerText.trim()) {
      lines.push(`— ${state.footerText}`);
    }
    if (state.messageLayout === 'full' && state.buttonsEnabled) {
      state.buttonRows
        .filter((r) => r.text.trim())
        .forEach((r) => lines.push(`[ ${r.text} ]`));
    }
    return lines;
  };

  return (
    <Modal open={open} onClose={onClose} size="fullscreen" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name={mode === 'create' ? 'plus' : 'edit'} />
        {mode === 'create' ? 'Criar modelo de WhatsApp' : 'Editar modelo de WhatsApp'}
      </Modal.Header>
      <Modal.Content scrolling style={{ minHeight: '70vh' }}>
        <Step.Group ordered unstackable style={{ marginBottom: '24px' }}>
          <Step
            active={state.step === 1}
            completed={state.step > 1}
            onClick={() => state.step > 1 && setStep(1)}
            style={{ cursor: state.step > 1 ? 'pointer' : 'default' }}
          >
            <Step.Content>
              <Step.Title>Configurações</Step.Title>
            </Step.Content>
          </Step>
          <Step
            active={state.step === 2}
            completed={state.step > 2}
            onClick={() => state.step > 2 && setStep(2)}
            style={{ cursor: state.step > 2 ? 'pointer' : 'default' }}
          >
            <Step.Content>
              <Step.Title>Tipo de mensagem</Step.Title>
            </Step.Content>
          </Step>
          <Step active={state.step === 3}>
            <Step.Content>
              <Step.Title>Conteúdo do modelo</Step.Title>
            </Step.Content>
          </Step>
        </Step.Group>

        {submitError && (
          <Message error onDismiss={() => setSubmitError(null)}>
            {submitError}
          </Message>
        )}

        {state.step === 1 && (
          <Grid stackable>
            <Grid.Column width={10}>
              <Message info>
                <Message.Header>Como funciona?</Message.Header>
                <p>
                  Preencha os dados para enviar o modelo à análise do WhatsApp. Você pode receber notificação por e-mail
                  quando o status mudar. A Zenvia exige o <strong>número</strong> (MSISDN) cadastrado no canal; o ID Meta
                  aparece no menu só como referência. Deixe em branco para usar o padrão configurado na API.
                </p>
              </Message>
              <Form>
                <Form.Field required>
                  <label>Nome do modelo</label>
                  <Input
                    value={state.name}
                    onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                    placeholder="nome_unico_template"
                    disabled={mode === 'edit'}
                  />
                </Form.Field>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Categoria</label>
                    <Dropdown
                      selection
                      options={CATEGORY_OPTIONS}
                      value={state.category}
                      onChange={(_, { value }) =>
                        setState((s) => ({ ...s, category: value as ZenviaTemplateCategory }))
                      }
                      menu={DROPDOWN_MENU_Z}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Idioma</label>
                    <Dropdown
                      selection
                      search
                      options={LOCALE_OPTIONS}
                      value={state.locale}
                      onChange={(_, { value }) => setState((s) => ({ ...s, locale: String(value) }))}
                      menu={DROPDOWN_MENU_Z}
                    />
                  </Form.Field>
                </Form.Group>
                <Form.Field>
                  <label>Selecione o Sender ID</label>
                  <Dropdown
                    selection
                    search
                    clearable
                    placeholder="Usar padrão da API ou escolher um remetente"
                    options={senderDropdownOptions}
                    value={state.senderId || undefined}
                    onChange={(_, { value }) =>
                      setState((s) => ({ ...s, senderId: value != null ? String(value) : '' }))
                    }
                    menu={DROPDOWN_MENU_Z}
                  />
                </Form.Field>
                <Form.Field>
                  <label>E-mail para notificação (separados por vírgula)</label>
                  <Input
                    value={state.notificationEmail}
                    onChange={(e) => setState((s) => ({ ...s, notificationEmail: e.target.value }))}
                    placeholder="a@empresa.com, b@empresa.com"
                  />
                </Form.Field>
              </Form>
            </Grid.Column>
            <Grid.Column width={6}>
              {help.title && (
                <Message info icon="info circle">
                  <Message.Content>
                    <Message.Header>{help.title}</Message.Header>
                    <p>{help.body}</p>
                  </Message.Content>
                </Message>
              )}
            </Grid.Column>
          </Grid>
        )}

        {state.step === 2 && (
          <div>
            <Header as="h3">Escolha o tipo da mensagem</Header>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Mensagem simples: só corpo (até 1024 caracteres) e variáveis. Completa: cabeçalho, rodapé e botões, como
              no fluxo Zenvia.
            </p>
            <Form>
              <Form.Field>
                <Radio
                  label="Mensagem simples — apenas corpo de texto (e variáveis)"
                  name="layout"
                  checked={state.messageLayout === 'simple'}
                  onChange={() =>
                    setState((s) => ({
                      ...s,
                      messageLayout: 'simple' as MessageLayout,
                      headerKind: 'none',
                      footerEnabled: false,
                      buttonsEnabled: false,
                    }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <Radio
                  label="Mensagem com cabeçalho, mídia, rodapé e botões"
                  name="layout"
                  checked={state.messageLayout === 'full'}
                  onChange={() => setState((s) => ({ ...s, messageLayout: 'full' as MessageLayout }))}
                />
              </Form.Field>
            </Form>
          </div>
        )}

        {state.step === 3 && (
          <Grid stackable>
            <Grid.Column width={10}>
              {state.messageLayout === 'full' && (
                <Segment basic>
                  <Header as="h4">Cabeçalho</Header>
                  <p style={{ color: '#666', fontSize: '0.9em' }}>
                    Arquivos de mídia precisam estar em URL pública. Informe a URL também nos exemplos (Meta/Zenvia).
                  </p>
                  <Form.Group inline>
                    {(
                      [
                        ['none', 'Sem cabeçalho'],
                        ['text', 'Texto'],
                        ['image', 'Imagem'],
                        ['document', 'Documento'],
                        ['video', 'Vídeo'],
                      ] as const
                    ).map(([k, label]) => (
                      <Form.Field key={k}>
                        <Radio
                          label={label}
                          name="headerKind"
                          checked={state.headerKind === k}
                          onChange={() =>
                            setState((s) => ({
                              ...s,
                              headerKind: k as HeaderKind,
                              headerText: k === 'text' ? s.headerText : '',
                              headerMediaUrl: ['image', 'document', 'video'].includes(k) ? s.headerMediaUrl : '',
                            }))
                          }
                        />
                      </Form.Field>
                    ))}
                  </Form.Group>
                  {state.headerKind === 'text' && (
                    <Form.Field>
                      <label>Texto do cabeçalho</label>
                      <Input
                        value={state.headerText}
                        maxLength={60}
                        onChange={(e) => setState((s) => ({ ...s, headerText: e.target.value }))}
                        placeholder="Até 60 caracteres"
                      />
                      <span style={{ fontSize: '0.85em', color: '#666' }}>
                        {headerRemaining} caracteres restantes
                      </span>
                    </Form.Field>
                  )}
                  {['image', 'document', 'video'].includes(state.headerKind) && (
                    <Form.Field>
                      <label>URL pública do arquivo</label>
                      <Input
                        value={state.headerMediaUrl}
                        onChange={(e) => setState((s) => ({ ...s, headerMediaUrl: e.target.value }))}
                        placeholder="https://..."
                      />
                    </Form.Field>
                  )}
                </Segment>
              )}

              <Segment basic>
                <Header as="h4">Corpo da mensagem {state.category !== 'AUTHENTICATION' && '*'}</Header>
                <p style={{ color: '#666', fontSize: '0.9em' }}>
                  Use {'{{1}}'}, {'{{nome}}'} etc. para variáveis. Preencha os exemplos abaixo.
                </p>
                <Form.Field required={state.category !== 'AUTHENTICATION'}>
                  <TextArea
                    rows={8}
                    value={state.bodyText}
                    onChange={(e) => setState((s) => ({ ...s, bodyText: e.target.value }))}
                    placeholder="Texto principal do modelo"
                  />
                  <span style={{ fontSize: '0.85em', color: bodyRemaining < 0 ? '#c00' : '#666' }}>
                    {bodyRemaining} caracteres restantes
                  </span>
                </Form.Field>
              </Segment>

              {state.messageLayout === 'full' && (
                <Segment basic>
                  <Header as="h4">Rodapé</Header>
                  <Form.Field>
                    <Radio
                      label="Sem rodapé"
                      checked={!state.footerEnabled}
                      onChange={() => setState((s) => ({ ...s, footerEnabled: false, footerText: '' }))}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Radio
                      label="Com texto"
                      checked={state.footerEnabled}
                      onChange={() => setState((s) => ({ ...s, footerEnabled: true }))}
                    />
                  </Form.Field>
                  {state.footerEnabled && (
                    <Form.Field>
                      <Input
                        value={state.footerText}
                        maxLength={60}
                        onChange={(e) => setState((s) => ({ ...s, footerText: e.target.value }))}
                        placeholder="Texto do rodapé"
                      />
                    </Form.Field>
                  )}
                </Segment>
              )}

              {state.messageLayout === 'full' && (
                <Segment basic>
                  <Header as="h4">Botões</Header>
                  <Form.Field>
                    <Radio
                      label="Sem botões"
                      checked={!state.buttonsEnabled}
                      onChange={() => setState((s) => ({ ...s, buttonsEnabled: false }))}
                    />
                  </Form.Field>
                  <Form.Field>
                    <Radio
                      label="Com botões"
                      checked={state.buttonsEnabled}
                      onChange={() => setState((s) => ({ ...s, buttonsEnabled: true }))}
                    />
                  </Form.Field>
                  {state.buttonsEnabled &&
                    state.buttonRows.map((row, idx) => (
                      <Segment key={idx} secondary>
                        <Form.Group widths="equal">
                          <Form.Field>
                            <label>Tipo</label>
                            <Dropdown
                              selection
                              options={[
                                { value: 'URL', text: 'URL' },
                                { value: 'PHONE_NUMBER', text: 'Telefone' },
                                { value: 'QUICK_REPLY', text: 'Resposta rápida' },
                              ]}
                              value={row.type}
                              menu={DROPDOWN_MENU_Z}
                              onChange={(_, { value }) => {
                                const next = [...state.buttonRows];
                                next[idx] = { ...next[idx], type: value as typeof row.type };
                                setState((s) => ({ ...s, buttonRows: next }));
                              }}
                            />
                          </Form.Field>
                          <Form.Field>
                            <label>Texto do botão</label>
                            <Input
                              value={row.text}
                              onChange={(e) => {
                                const next = [...state.buttonRows];
                                next[idx] = { ...next[idx], text: e.target.value };
                                setState((s) => ({ ...s, buttonRows: next }));
                              }}
                            />
                          </Form.Field>
                        </Form.Group>
                        {row.type === 'URL' && (
                          <Form.Field>
                            <label>URL</label>
                            <Input
                              value={row.url}
                              onChange={(e) => {
                                const next = [...state.buttonRows];
                                next[idx] = { ...next[idx], url: e.target.value };
                                setState((s) => ({ ...s, buttonRows: next }));
                              }}
                            />
                          </Form.Field>
                        )}
                        {row.type === 'PHONE_NUMBER' && (
                          <Form.Field>
                            <label>Telefone</label>
                            <Input
                              value={row.phoneNumber}
                              onChange={(e) => {
                                const next = [...state.buttonRows];
                                next[idx] = { ...next[idx], phoneNumber: e.target.value };
                                setState((s) => ({ ...s, buttonRows: next }));
                              }}
                            />
                          </Form.Field>
                        )}
                        {row.type === 'QUICK_REPLY' && (
                          <Form.Field>
                            <label>Payload</label>
                            <Input
                              value={row.payload}
                              onChange={(e) => {
                                const next = [...state.buttonRows];
                                next[idx] = { ...next[idx], payload: e.target.value };
                                setState((s) => ({ ...s, buttonRows: next }));
                              }}
                            />
                          </Form.Field>
                        )}
                        <Button
                          type="button"
                          size="small"
                          negative
                          disabled={state.buttonRows.length <= 1}
                          onClick={() => {
                            const next = state.buttonRows.filter((_, i) => i !== idx);
                            setState((s) => ({ ...s, buttonRows: next.length ? next : s.buttonRows }));
                          }}
                        >
                          Remover
                        </Button>
                      </Segment>
                    ))}
                  {state.buttonsEnabled && (
                    <Button
                      type="button"
                      size="small"
                      onClick={() =>
                        setState((s) => ({
                          ...s,
                          buttonRows: [
                            ...s.buttonRows,
                            { type: 'URL', text: '', url: '', phoneNumber: '', payload: '' },
                          ],
                        }))
                      }
                    >
                      + Botão
                    </Button>
                  )}
                </Segment>
              )}

              {varsInTexts.length > 0 && (
                <Segment basic>
                  <Header as="h4">Exemplos das variáveis (Meta / Zenvia)</Header>
                  <Message info size="small">
                    Cada variável no texto precisa de um exemplo não vazio para aprovação.
                  </Message>
                  {varsInTexts.map((v) => (
                    <Form.Field key={v}>
                      <label>{`{{${v}}}`}</label>
                      <Input
                        value={state.variableExamples[v] ?? ''}
                        onChange={(e) =>
                          setState((s) => ({
                            ...s,
                            variableExamples: { ...s.variableExamples, [v]: e.target.value },
                          }))
                        }
                        placeholder={`Exemplo para ${v}`}
                      />
                    </Form.Field>
                  ))}
                </Segment>
              )}
            </Grid.Column>
            <Grid.Column width={6}>
              <Segment style={{ background: '#f5f6f7', minHeight: 280 }}>
                <Header as="h5" style={{ marginTop: 0 }}>
                  Prévia
                </Header>
                <div
                  style={{
                    background: '#e7f7ed',
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 14,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {previewLines().join('\n')}
                </div>
              </Segment>
            </Grid.Column>
          </Grid>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        {state.step > 1 && (
          <Button onClick={goPrev} disabled={loading}>
            Anterior
          </Button>
        )}
        {state.step < 3 && (
          <Button primary onClick={goNext} disabled={loading}>
            Próximo
          </Button>
        )}
        {state.step === 3 && (
          <Button primary onClick={handleSubmit} loading={loading} disabled={loading}>
            {mode === 'create' ? 'Criar template' : 'Salvar alterações'}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default WhatsAppTemplateWizard;
